/**
 * Firebase Remote Config service.
 * Fetches and provides runtime config values (goals_enabled, etc.).
 * Configure these parameters in Firebase Console > Remote Config.
 */

import { remoteConfig } from './firebase';
import { fetchAndActivate, getValue } from 'firebase/remote-config';
import type { ExperimentConfig } from './experimentAssignment';


export const REMOTE_CONFIG_KEYS = {
  GOALS_ENABLED: 'goals_enabled',
  SUBSCRIPTIONS_ENABLED: 'subscriptions_enabled',
  PAYMENTS_ENABLED: 'payments_enabled',
  CART_COMPANION_ENABLED: 'cart_companion_enabled',
  /** Second Look brief in Cart Companion (paid tier). Replaces the retired Personal Shopper. */
  CART_COMPANION_SECOND_LOOK_ENABLED: 'cart_companion_second_look_enabled',
  PUSH_SOFT_ASK_ENABLED: 'push_soft_ask_enabled',
  /** JSON config for the push soft-ask orchestrator: per-surface
   *  {enabled, cooldownDays, priority} + global {globalDeclineCooldownDays,
   *  maxLifetimeAsks}. Raw string here — parsed with client-side defaults in
   *  apps/enough/src/services/pushPermissionOrchestrator.ts, so absent/empty
   *  means "defaults" (today's behavior). push_soft_ask_enabled stays the
   *  master kill-switch; both must allow. */
  PUSH_SOFT_ASK_CONFIG: 'push_soft_ask_config',
  /** Soft floor — below it the dismissible UpdateBanner shows. Suggests. */
  MIN_IOS_VERSION: 'min_ios_version',
  MIN_ANDROID_VERSION: 'min_android_version',
  /**
   * Hard floor — below it the blocking ForceUpdateGate shows (plus Google's
   * native immediate-update flow on Android). Enforces. Empty = no wall.
   * Convention: set at a major boundary (e.g. `2.0.0`). See lib/versionGate.ts.
   */
  FORCE_UPDATE_BELOW_IOS: 'force_update_below_ios',
  FORCE_UPDATE_BELOW_ANDROID: 'force_update_below_android',
  /** When true, home feed renders `contentType: insight` focus items. Server delivery can run regardless. */
  INSIGHTS_FEED_ENABLED: 'insights_feed_enabled',
  DEBT_MANAGER_ENABLED: 'debt_manager_enabled',
  /** Gates the Quiet Days feature itself (route, nav entries, Pause page tool). */
  QUIET_DAYS_ENABLED: 'quiet_days_enabled',
  /** REPURPOSED (Jun 2026): whether No-Spend Weekend EVENT features are live —
   *  countdown banners/cards and free-user enablement. The Quiet Days feature
   *  is gated by quiet_days_enabled. */
  NO_SPEND_WEEKEND_ENABLED: 'no_spend_weekend_enabled',
  NO_SPEND_LIVE_ACTIVITY_ENABLED: 'no_spend_live_activity_enabled',
  NO_SPEND_ANDROID_REMINDER_ENABLED: 'no_spend_android_reminder_enabled',
  MFA_ENABLED: 'mfa_enabled',
  // pause_page_enabled: still SERVED in Remote Config for old binaries but no
  // longer read here — the Pause page is hard-coded on (2026-09).
  /** "Needs your attention" anomaly triage in the Subscription Finder
   *  (possibly-canceled, amount spikes, recently detected). Off by default —
   *  we test into it. */
  SUBSCRIPTION_ANOMALIES_ENABLED: 'subscription_anomalies_enabled',
  /** Kill switch for the "Are you enjoying enough?" → app store review prompt. */
  APP_REVIEW_PROMPT_ENABLED: 'app_review_prompt_enabled',
  /** Distinct activity days required before the review prompt can appear. */
  APP_REVIEW_MIN_ACTIVITY_DAYS: 'app_review_min_activity_days',
  /** Global experiment kill switch. When true, every A/B experiment resolves to
   *  control (champion) regardless of its Firestore descriptor — the fast,
   *  no-deploy, no-Firestore-dependency emergency lever. */
  EXPERIMENTS_KILL_SWITCH: 'experiments_kill_switch',
  /** Shareable cards: render a progress/goal/pause win as a branded image the
   *  member can post or send (share composer sheet). No dollar amounts or PII
   *  ever leave the app. Off by default — we test into it. */
  SHARE_CARDS_ENABLED: 'share_cards_enabled',
  /** App Check client init rollout (PROD builds only; dev builds never init App
   *  Check regardless). Default OFF. NOT read via RemoteConfigContext — resolved
   *  pre-React in packages/shared/src/services/appCheck.ts, which also honors a
   *  per-user `featureOverrides/{uid}` boolean of the same name (override ?? global).
   *  Doubles as the no-deploy kill switch. See docs/operations/2026/Q3/APP_CHECK_ROLLOUT.md. */
  APP_CHECK_CLIENT_ENABLED: 'app_check_client_enabled',
  /** Free-account-limit 2→1 cohort cutoff (ISO 8601 instant). Empty = disabled,
   *  everyone stays on the legacy two-slot experience. Users whose userContent
   *  createdAt is AFTER this instant (and who never held a legacy designation)
   *  resolve to the single-account cohort, sticky for life. Operational rule:
   *  set this only after the app version carrying the single-account
   *  experience is broadly available, and never move it earlier. */
  SINGLE_ACCOUNT_CUTOFF: 'single_account_cutoff',
  /** "Where It Goes" cash-flow cascade (/flow). Hidden while the projection
   *  engine is validated behind the scenes — expect this to stay off in prod
   *  for months; enable per-tester via featureOverrides/{uid}. */
  WHERE_IT_GOES_ENABLED: 'where_it_goes_enabled',
  /** Found-money ledger Home card ("enough has found you $X") — reads the
   *  server-written userContent/{uid}/stats/foundMoney rollup. Off by
   *  default; dev-alpha only until the ledger has soaked (spec 04a). */
  FOUND_MONEY_ENABLED: 'found_money_enabled',
  // paid_launch_enabled: still SERVED in Remote Config for legacy ≤1.42
  // binaries (flag-off there; retires at G4) but not read by this client —
  // v1.43 hardcoded the cutover on, and v1.58 removed the read path entirely.
  /** Understanding Loop: the understanding moment — one brief + one
   *  next-best-action + the forward next-session hook. ONE feature with two
   *  placements of the same text (Daily Session close on Home, and leading
   *  Your Story), so one flag gates both; the retired
   *  understanding_story_brief_enabled key folded into this one (2026-08-24).
   *  Off by default; the server has its own gate
   *  (appConfig/understandingLoop.momentEnabled) — flip both to launch, and
   *  either alone kills it. Users with AI disabled never see it regardless. */
  UNDERSTANDING_MOMENT_ENABLED: 'understanding_moment_enabled',
  /** First-party "enough. membership" row in the subscription lists (Finder +
   *  home carousel) — what we cost, shown alongside everything else the member
   *  pays for. Off by default: with the flag off the lists are exactly what
   *  Plaid detected, unchanged. We test into this surface. */
  MEMBERSHIP_ROW_ENABLED: 'membership_row_enabled',
} as const;

export interface RemoteConfigValues {
  goalsEnabled: boolean;
  subscriptionsEnabled: boolean;
  paymentsEnabled: boolean;
  cartCompanionEnabled: boolean;
  cartCompanionSecondLookEnabled: boolean;
  pushSoftAskEnabled: boolean;
  /** Raw push_soft_ask_config JSON string ('' = use client defaults). */
  pushSoftAskConfig: string;
  minIosVersion: string;
  minAndroidVersion: string;
  forceUpdateBelowIos: string;
  forceUpdateBelowAndroid: string;
  insightsFeedEnabled: boolean;
  debtManagerEnabled: boolean;
  quietDaysEnabled: boolean;
  noSpendWeekendEnabled: boolean;
  noSpendLiveActivityEnabled: boolean;
  noSpendAndroidReminderEnabled: boolean;
  mfaEnabled: boolean;
  subscriptionAnomaliesEnabled: boolean;
  appReviewPromptEnabled: boolean;
  appReviewMinActivityDays: number;
  experimentsKillSwitch: boolean;
  shareCardsEnabled: boolean;
  singleAccountCutoff: string;
  whereItGoesEnabled: boolean;
  foundMoneyEnabled: boolean;
  understandingMomentEnabled: boolean;
  membershipRowEnabled: boolean;
}

/**
 * Read a champion/challenger experiment config from Remote Config.
 *
 * Each experiment is its OWN Remote Config param, named by its experiment key
 * (e.g. `first_session_v2`), holding a JSON config:
 *   { id, active, allocation: { variantKey: weight } }
 * Parsed defensively — a missing/malformed param means "no experiment" (control).
 *
 * Call only after `fetchRemoteConfig()` has activated; before that, server params
 * aren't present and this returns null.
 */
export const getExperimentConfig = (experimentKey: string): ExperimentConfig | null => {
  const raw = getValue(remoteConfig, experimentKey).asString();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && typeof parsed.allocation === 'object') {
      return parsed as ExperimentConfig;
    }
    return null;
  } catch (err) {
    console.warn(`Failed to parse experiment config "${experimentKey}", ignoring:`, err);
    return null;
  }
};

/**
 * Fetch latest config from Firebase and activate it.
 * Uses in-app defaults until fetch completes.
 * Includes a 5s timeout to prevent hanging when Firebase is unreachable.
 */
export const fetchRemoteConfig = async (): Promise<boolean> => {
  try {
    const fetchPromise = fetchAndActivate(remoteConfig);
    const timeout = new Promise<boolean>((_, reject) =>
      setTimeout(() => reject(new Error('Remote Config fetch timed out')), 5000)
    );
    return await Promise.race([fetchPromise, timeout]);
  } catch (err) {
    console.warn('Remote Config fetch failed, using defaults:', err);
    return false;
  }
};

/**
 * Get current config values (from cache or defaults).
 * Call fetchRemoteConfig() first to get latest from server.
 */
export const getRemoteConfigValues = (): RemoteConfigValues => {
  const goalsEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.GOALS_ENABLED).asString() === 'true';
  const subscriptionsEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.SUBSCRIPTIONS_ENABLED).asString() === 'true';
  const paymentsEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.PAYMENTS_ENABLED).asString() === 'true';
  const cartCompanionEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.CART_COMPANION_ENABLED).asString() === 'true';
  const cartCompanionSecondLookEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.CART_COMPANION_SECOND_LOOK_ENABLED).asString() === 'true';
  const pushSoftAskEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.PUSH_SOFT_ASK_ENABLED).asString() === 'true';
  const pushSoftAskConfig = getValue(remoteConfig, REMOTE_CONFIG_KEYS.PUSH_SOFT_ASK_CONFIG).asString() || '';
  const minIosVersion = getValue(remoteConfig, REMOTE_CONFIG_KEYS.MIN_IOS_VERSION).asString() || '';
  const minAndroidVersion = getValue(remoteConfig, REMOTE_CONFIG_KEYS.MIN_ANDROID_VERSION).asString() || '';
  const forceUpdateBelowIos = getValue(remoteConfig, REMOTE_CONFIG_KEYS.FORCE_UPDATE_BELOW_IOS).asString() || '';
  const forceUpdateBelowAndroid = getValue(remoteConfig, REMOTE_CONFIG_KEYS.FORCE_UPDATE_BELOW_ANDROID).asString() || '';
  const insightsFeedEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.INSIGHTS_FEED_ENABLED).asString() === 'true';
  const debtManagerEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.DEBT_MANAGER_ENABLED).asString() === 'true';
  const quietDaysEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.QUIET_DAYS_ENABLED).asString() === 'true';
  const noSpendWeekendEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.NO_SPEND_WEEKEND_ENABLED).asString() === 'true';
  const noSpendLiveActivityEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.NO_SPEND_LIVE_ACTIVITY_ENABLED).asString() === 'true';
  const noSpendAndroidReminderEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.NO_SPEND_ANDROID_REMINDER_ENABLED).asString() === 'true';
  const mfaEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.MFA_ENABLED).asString() === 'true';
  const subscriptionAnomaliesEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.SUBSCRIPTION_ANOMALIES_ENABLED).asString() === 'true';
  const appReviewPromptEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.APP_REVIEW_PROMPT_ENABLED).asString() === 'true';
  const appReviewMinActivityDays = getValue(remoteConfig, REMOTE_CONFIG_KEYS.APP_REVIEW_MIN_ACTIVITY_DAYS).asNumber() || 10;
  const experimentsKillSwitch = getValue(remoteConfig, REMOTE_CONFIG_KEYS.EXPERIMENTS_KILL_SWITCH).asString() === 'true';
  const shareCardsEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.SHARE_CARDS_ENABLED).asString() === 'true';
  const singleAccountCutoff = getValue(remoteConfig, REMOTE_CONFIG_KEYS.SINGLE_ACCOUNT_CUTOFF).asString() || '';
  const whereItGoesEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.WHERE_IT_GOES_ENABLED).asString() === 'true';
  const foundMoneyEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.FOUND_MONEY_ENABLED).asString() === 'true';
  const understandingMomentEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.UNDERSTANDING_MOMENT_ENABLED).asString() === 'true';
  const membershipRowEnabled = getValue(remoteConfig, REMOTE_CONFIG_KEYS.MEMBERSHIP_ROW_ENABLED).asString() === 'true';
  return {
    goalsEnabled,
    subscriptionsEnabled,
    paymentsEnabled,
    cartCompanionEnabled,
    cartCompanionSecondLookEnabled,
    pushSoftAskEnabled,
    pushSoftAskConfig,
    minIosVersion,
    minAndroidVersion,
    forceUpdateBelowIos,
    forceUpdateBelowAndroid,
    insightsFeedEnabled,
    debtManagerEnabled,
    quietDaysEnabled,
    noSpendWeekendEnabled,
    noSpendLiveActivityEnabled,
    noSpendAndroidReminderEnabled,
    mfaEnabled,
    subscriptionAnomaliesEnabled,
    appReviewPromptEnabled,
    appReviewMinActivityDays,
    experimentsKillSwitch,
    shareCardsEnabled,
    singleAccountCutoff,
    whereItGoesEnabled,
    foundMoneyEnabled,
    understandingMomentEnabled,
    membershipRowEnabled,
  };
};
